import { GameState, GovernmentRecord, LobbyState, Role } from "../types";

export type Award = {
  id: string;
  /** A small picture for the card. */
  icon: string;
  title: string;
  /** What the award means, in a sentence. */
  blurb: string;
  /** Everyone who earned it (a tie shares it). */
  winners: string[];
  /** The numbers behind it. */
  stat: string;
};

const plural = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);

/** The players with the highest count, if it reaches the minimum. */
function top(counts: Record<string, number>, min: number): { names: string[]; value: number } | undefined {
  let best = 0;
  Object.values(counts).forEach((v) => {
    if (v > best) best = v;
  });
  if (best < min) return undefined;
  return { names: Object.keys(counts).filter((n) => counts[n] === best), value: best };
}

/** The government that won the game by its policy or by electing Hitler, if there was one. */
export function winningGovernment(game: GameState): GovernmentRecord | undefined {
  const records = game.history ?? [];
  const last = records[records.length - 1];
  if (!last || last.votes === undefined) {
    return undefined; // won by an execution, or by a policy the election tracker enacted
  }
  const policyWin = game.state === LobbyState.FASCIST_VICTORY_POLICY || game.state === LobbyState.LIBERAL_VICTORY_POLICY;
  if (policyWin && (last.outcome === "LIBERAL" || last.outcome === "FASCIST")) return last;
  if (game.state === LobbyState.FASCIST_VICTORY_ELECTION && last.outcome === "HITLER_ELECTED") return last;
  return undefined;
}

/**
 * The funny and impressive numbers of a finished game, worked out from the vote history and the revealed roles.
 * An award only appears when it was really earned (each has a minimum), and a tie shares it.
 */
export function computeAwards(game: GameState): Award[] {
  const records = (game.history ?? []).filter((r) => r.votes !== undefined) as (GovernmentRecord & {
    votes: Record<string, boolean>;
  })[];
  const names = game.playerOrder ?? [];
  const role = (n: string) => game.players[n]?.id;
  const isFascistTeam = (n: string) => role(n) === Role.FASCIST || role(n) === Role.HITLER;
  const isLiberal = (n: string) => role(n) === Role.LIBERAL;
  const zero = (): Record<string, number> => Object.fromEntries(names.map((n) => [n, 0]));
  const awards: Award[] = [];

  // The Snake: a Fascist who kept voting Ja on governments that enacted Liberal policies.
  {
    const c = zero();
    records.filter((r) => r.outcome === "LIBERAL").forEach((r) =>
      names.forEach((n) => {
        if (isFascistTeam(n) && r.votes[n] === true) c[n]++;
      })
    );
    const t = top(c, 2);
    if (t) {
      awards.push({
        id: "snake", icon: "🐍", title: "The Snake",
        blurb: "A Fascist who voted Ja on the most Liberal governments.",
        winners: t.names, stat: "Ja on " + plural(t.value, "Liberal policy", "Liberal policies"),
      });
    }
  }

  // The Paranoiac: a Liberal who voted Nein on governments that went on to enact Liberal policies.
  {
    const c = zero();
    records.filter((r) => r.outcome === "LIBERAL").forEach((r) =>
      names.forEach((n) => {
        if (isLiberal(n) && r.votes[n] === false) c[n]++;
      })
    );
    const t = top(c, 2);
    if (t) {
      awards.push({
        id: "paranoiac", icon: "😰", title: "The Paranoiac",
        blurb: "A Liberal who voted Nein on the most successful Liberal governments.",
        winners: t.names, stat: "Nein on " + plural(t.value, "Liberal policy", "Liberal policies"),
      });
    }
  }

  // The Ghost: the longest run of governments, from the start or after the last one in office, without being
  // President or Chancellor. Only governments that were voted in count, and only while the player could vote.
  {
    const best: Record<string, number> = zero();
    names.forEach((n) => {
      let run = 0;
      records.forEach((r) => {
        if (r.outcome === "REJECTED") return; // nobody took office
        if (r.votes[n] === undefined) {
          run = 0; // not at the table (executed)
          return;
        }
        if (r.president === n || r.chancellor === n) {
          run = 0;
        } else {
          run++;
          if (run > best[n]) best[n] = run;
        }
      });
    });
    const t = top(best, 3);
    if (t) {
      awards.push({
        id: "ghost", icon: "👻", title: "The Ghost",
        blurb: "Went the longest without holding office.",
        winners: t.names, stat: plural(t.value, "government", "governments") + " in a row without office",
      });
    }
  }

  // The Kingmaker: the Ja voters of the winning government when it passed by the narrowest margin there is,
  // so that any one of them changing their vote would have changed the game.
  {
    const win = winningGovernment(game);
    if (win && win.votes) {
      const ja = Object.keys(win.votes).filter((n) => win.votes![n] === true);
      const nein = Object.keys(win.votes).filter((n) => win.votes![n] === false);
      if (ja.length > nein.length && ja.length - nein.length <= 2) {
        awards.push({
          id: "kingmaker", icon: "👑", title: "The Kingmaker",
          blurb: "The deciding vote on the government that won the game.",
          winners: ja, stat: "Passed " + ja.length + "–" + nein.length + ", so every Ja counted",
        });
      }
    }
  }

  // The Contrarian: the player who most often voted against how the table decided.
  {
    const c = zero();
    records.forEach((r) => {
      const ja = names.filter((n) => r.votes[n] === true).length;
      const nein = names.filter((n) => r.votes[n] === false).length;
      const passed = ja > nein;
      names.forEach((n) => {
        if (r.votes[n] !== undefined && r.votes[n] !== passed) c[n]++;
      });
    });
    const t = top(c, 3);
    if (t) {
      awards.push({
        id: "contrarian", icon: "⚡", title: "The Contrarian",
        blurb: "Voted against the result more often than anyone.",
        winners: t.names, stat: "On the losing side of " + plural(t.value, "vote", "votes"),
      });
    }
  }

  // The Hitler Whisperer: a Fascist who kept backing governments with Hitler as Chancellor.
  {
    const c = zero();
    records.filter((r) => r.chancellor !== undefined && role(r.chancellor) === Role.HITLER).forEach((r) =>
      names.forEach((n) => {
        if (role(n) === Role.FASCIST && r.votes[n] === true) c[n]++;
      })
    );
    const t = top(c, 2);
    if (t) {
      awards.push({
        id: "whisperer", icon: "🤫", title: "The Hitler Whisperer",
        blurb: "A Fascist who voted Ja on the most governments with Hitler as Chancellor.",
        winners: t.names, stat: "Ja on " + plural(t.value, "Hitler government", "Hitler governments"),
      });
    }
  }

  // The Optimist: a Liberal who kept trusting governments that enacted Fascist policies.
  {
    const c = zero();
    records.filter((r) => r.outcome === "FASCIST").forEach((r) =>
      names.forEach((n) => {
        if (isLiberal(n) && r.votes[n] === true) c[n]++;
      })
    );
    const t = top(c, 2);
    if (t) {
      awards.push({
        id: "optimist", icon: "😇", title: "The Optimist",
        blurb: "A Liberal who voted Ja on the most governments that enacted Fascist policies.",
        winners: t.names, stat: "Ja on " + plural(t.value, "Fascist policy", "Fascist policies"),
      });
    }
  }

  return awards;
}

/** The end screen shows at most this many awards. */
export const MAX_AWARDS = 3;

/** Returns at most `max` of the earned awards; if more were earned, a random selection (kept in their original order). */
export function pickAwards(awards: Award[], max = MAX_AWARDS, rng: () => number = Math.random): Award[] {
  if (awards.length <= max) {
    return awards;
  }
  const order = awards.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order
    .slice(0, max)
    .sort((a, b) => a - b)
    .map((i) => awards[i]);
}
